import { useState } from "react"
import { format, isValid, parseISO, startOfDay } from "date-fns"
import { uk } from "date-fns/locale"
import { CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  formControlLengthClasses,
  type FormControlLength,
} from "@/components/ui/form-control-length"

/**
 * design-system.md's `DatePicker` — a `Popover` + `Calendar(mode="single")`
 * composition; there is no droppable `date-picker` registry item. Takes and
 * returns an ISO `yyyy-MM-dd` string, not a `Date`, matching every call
 * site's native-`<input type="date">` predecessor and how the mocks store
 * dates. An unparseable or empty `value` renders the placeholder rather than
 * an invalid date. `captionLayout="dropdown"` swaps the month/year caption
 * for select dropdowns (react-day-picker's own default range with no
 * `startMonth`/`endMonth` given — 100 years back from today through the end
 * of this year) so a far-off date like a birthdate doesn't need 1000+ clicks
 * of the prev-month chevron to reach.
 */
export function DatePicker({
  value,
  onChange,
  placeholder = "Оберіть дату",
  id,
  className,
  disabled,
  disableFuture = false,
  disablePast = false,
  length = "full-size",
}: {
  value?: string | null
  onChange: (value: string) => void
  placeholder?: string
  id?: string
  className?: string
  disabled?: boolean
  /** Blocks picking/navigating to a date later than today — e.g. "Дата реєстрації" can't be in the future. */
  disableFuture?: boolean
  /** Blocks picking/navigating to a date earlier than today — e.g. "Дата чергового проходження ОТК" can't be in the past. */
  disablePast?: boolean
  length?: FormControlLength
}) {
  const [open, setOpen] = useState(false)
  const parsed = value ? parseISO(value) : undefined
  const selected = parsed && isValid(parsed) ? parsed : undefined
  const today = new Date()
  // `disablePast`'s "before today" must not exclude today itself — react-day-picker's
  // `before` matcher compares exact instants, and `today` still carries the current
  // time-of-day, so an un-normalized value would disable today after midnight passes.
  const startOfToday = startOfDay(today)

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            "w-full justify-start font-normal",
            formControlLengthClasses[length],
            className
          )}
          disabled={disabled}
          id={id}
          variant="outline"
        >
          <CalendarIcon data-icon="inline-start" />
          {selected ? (
            format(selected, "dd.MM.yyyy")
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          captionLayout="dropdown"
          disabled={
            disableFuture
              ? { after: today }
              : disablePast
                ? { before: startOfToday }
                : undefined
          }
          locale={uk}
          mode="single"
          onSelect={(date) => {
            if (!date) return
            // Backstops the `disabled` matcher above — belt-and-suspenders
            // against ever committing a value that violates the active
            // constraint, regardless of how the day got selected.
            if (disableFuture && date > today) return
            if (disablePast && date < startOfToday) return
            onChange(format(date, "yyyy-MM-dd"))
            setOpen(false)
          }}
          selected={selected}
          weekStartsOn={1}
        />
      </PopoverContent>
    </Popover>
  )
}
